// Rules the server applies and the app previews or reads, kept once here so the
// two cannot drift (code review D-04, 2026-10-17). Each was copied by hand into
// the app before; the copies are gone.
import { FLEET_COMPANY_ID } from './constants.js'

/**
 * The RULE-SCOPE match key, and the per-vendor `precedent_candidates`
 * informational key: lowercase; `.` `,` `-` `/` `&` `#` to spaces; DELETE EVERY
 * DIGIT (which kills embedded dates, PO numbers and meter readings); collapse
 * whitespace; trim.
 *
 * NOT the GL-precedent EXACT-match key — that is `precedentKeyExact` in
 * `backend/src/domain/matchingLadder.ts` (owner ruling, 2026-08-16 change set,
 * item 5: a different SIZE of the same product must fall through to a fuzzy
 * SUGGESTION, so the auto-applying rung cannot be digit-blind). This function
 * backs `description_scope` matching and must not change: a stored rule scope
 * must keep matching exactly as it did. The Rules → Codes pop-up previews a
 * scope with it; the server re-normalizes whatever it is sent.
 */
export function normalizeDescription(value: string | null | undefined): string {
  if (!value) return ''
  return value
    .toLowerCase()
    .replace(/[.,\-/&#]/g, ' ')
    .replace(/[0-9]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * HOW A VENDOR OR COMPANY ALIAS IS STORED: trimmed, internal whitespace
 * collapsed, and never trusted from the client. Case is kept for display,
 * because matching folds it anyway (`vendor_aliases_alias_lower_idx` and
 * `lowerTrim` for a vendor alias, `companyKey` for a company alias); whitespace
 * is not, because a stray leading or trailing space would silently stop a
 * stored alias from matching an as-printed name. One rule for both alias routes
 * (code review G-10) and the Rules screen's preview.
 */
export function normalizeAliasName(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ')
}

/**
 * Each AI pass's "without issue" sentence (owner, 2026-09-30 — D-250),
 * verbatim from the owner apart from the apostrophe, which is the curly one
 * every note the system writes uses. Each prompt asks for exactly its sentence;
 * the host writes it when the pass's note came back empty anyway.
 */
export const SPLITTING_NOTE_DEFAULT = 'The invoice’s start and end points were identified without issue.'
export const EXTRACTING_NOTE_DEFAULT = 'The invoice’s content was extracted without issue.'
export const MATCHING_NOTE_DEFAULT = 'The invoice’s content was recognized and coded without issue.'

const AI_NOTE_DEFAULTS: readonly string[] = [SPLITTING_NOTE_DEFAULT, EXTRACTING_NOTE_DEFAULT, MATCHING_NOTE_DEFAULT]

/** True when a note is one pass's default sentence and says nothing more. Either apostrophe. */
export function isDefaultAiNote(note: string): boolean {
  return AI_NOTE_DEFAULTS.includes(note.trim().replace(/['‘]/g, '’'))
}

/** Which Additional Detail a line owes. A line may owe both (D-190). */
export interface AdditionalDetailSections {
  marketing: boolean
  transportation: boolean
}

/**
 * THE ONE PLACE the two Additional Detail triggers are expressed (owner,
 * 2026-09-28 — D-190):
 *
 *  - MARKETING: the line's settled account carries `detail_kind: 'marketing'`
 *    (`67-518` today) — data, never a hard-coded account id.
 *  - TRANSPORTATION: the INVOICE's company is `FLEET_COMPANY_ID`
 *    (`PACE Healthcare Transportation`, company `156`) — by id, never by name,
 *    because a company can be renamed. A line has no company of its own (code
 *    review B-05, 2026-10-03), and the account plays no part.
 *
 * The two are independent, so a fleet line coded to a Marketing account owes
 * both. Matching, the required-fields check and every screen read this.
 */
export function additionalDetailSections(
  accountKind: string | null | undefined,
  invoiceCompanyId: string | null | undefined,
): AdditionalDetailSections {
  return {
    marketing: accountKind === 'marketing',
    transportation: invoiceCompanyId === FLEET_COMPANY_ID,
  }
}
